import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { hapticsImpactLight } from '../../common/haptic';
import IonicModal from './IonicModal';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import CloseIcon from '../../assets/icons/close.svg?react';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme.background.overlay}; /* Dark overlay */
  z-index: 1000; /* Higher z-index to sit above other elements */
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContainer = styled.div`
  min-width: 450px;
  max-width: 700px;
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  position: relative;
  z-index: 1001;
  box-shadow: ${({ theme }) => theme.special.modalShadow}; /* Modal shadow */
  @media ${PHONE_MEDIA_QUERY} {
    min-width: 0;
    width: 90%;
    max-height: 90vh;
    overflow-y: scroll;
  }

  ${({ $width }) =>
    $width &&
    `
    width: ${$width};
    min-width: ${$width};
  `}
`;

const BackContainer = styled.div`
  border-radius: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.text.title};
  align-items: center;
  cursor: pointer;
  gap: 8px;
`;

const CloseContainer = styled(BackContainer)`
  margin-left: auto;
`;

const ScrollContainer = styled.div`
  height: fit-content;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const ModalContent = styled.div`
  padding: 10px 10px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 0px;
  }
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const FirstRow = styled.div`
  display: flex;
  padding: 10px 20px;
  top: 0px;
  width: 100%;
  position: absolute;
`;

const BackText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text.title};
`;

export const FullHeightModalContent = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  max-width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 90vh;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 20px;
    text-align: left;
    height: calc(
      100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 20px -
        10px
    );
    max-width: 100%;
  }
`;

const Modal = ({
  children,
  onClose,
  width,
  onBack = null,
  customStyle = {},
  customOverlayStyle = {},
  breakpoints = [0],
  initialBreakpoint = 0,
  header = '',
  onDidPresent = () => {},
  cssClass = '',
}) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);

  const theme = useTheme();

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClickOutside = (event) => {
    if (event.target.id === 'modal-overlay') {
      onClose();
    }
  };

  useEffect(() => {
    if (!isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMobile]);

  if (isMobile) {
    return (
      <IonicModal
        onDidPresent={onDidPresent}
        onClose={onClose}
        onBack={onBack}
        width={width}
        customStyle={customStyle}
        customOverlayStyle={customOverlayStyle}
        header={header}
        breakpoints={breakpoints}
        initialBreakpoint={initialBreakpoint}
        cssClass={cssClass}
      >
        {children}
      </IonicModal>
    );
  }

  return (
    <Overlay style={customOverlayStyle} id='modal-overlay'>
      <ModalContainer ref={modalRef} $width={width} style={customStyle || {}}>
        <ScrollContainer>
          <FirstRow>
            {onBack && (
              <BackContainer
                onClick={() => {
                  hapticsImpactLight();
                  onBack();
                }}
              >
                <ChevronLeftIcon
                  width={15}
                  height={15}
                  style={{ color: theme.text.title }}
                />
                <BackText>{t('common.back')}</BackText>
              </BackContainer>
            )}
            {onClose && (
              <CloseContainer
                onClick={() => {
                  hapticsImpactLight();
                  onClose();
                }}
              >
                <CloseIcon
                  width={20}
                  height={20}
                  style={{ color: theme.text.title }}
                />
              </CloseContainer>
            )}
          </FirstRow>
          <ModalContent>{children}</ModalContent>
        </ScrollContainer>
      </ModalContainer>
    </Overlay>
  );
};

export default Modal;
